import {useEmojisQuery} from '@github-ui/emoji-autocomplete/use-emojis-query'
import {InlineAutocomplete} from '@github-ui/inline-autocomplete'
import {useAutocompleteTriggersAndSuggestions} from '@github-ui/inline-autocomplete/hooks/use-autocomplete-triggers-and-suggestions'
import {useEmojiSuggestions} from '@github-ui/markdown-editor/suggestions/use-emoji-suggestions'
import {useMentionSuggestions} from '@github-ui/markdown-editor/suggestions/use-mention-suggestions'
import {getUtf8StringLength} from '@github-ui/text'
import {TextInput} from '@primer/react'
import {forwardRef, useCallback, useMemo, useState} from 'react'

import {useMentionsQuery} from '../hooks/use-mentions-query'

export interface EditStatusAutoCompleteProps {
  /** Initial value for the status message input */
  defaultValue: string
  /** Callback fired when the character count changes */
  onCharacterCountChange: (value: number) => void
  /** A user preference setting that controls the tone of Emojis */
  tone: number
  /** The organization context for mention suggestions */
  organization?: {
    globalRelayId?: string
  }
  /** Callback to set the input value in the parent component */
  setInputValue?: (value: string) => void
}

const SUGGESTION_TYPES = {
  EMOJI: 'emoji',
  MENTIONS: 'mentions',
}

export const MESSAGE_MAX_LENGTH = 80

const emptyArray: [] = [] // constant reference to avoid re-running effects

export const EditStatusAutoComplete = forwardRef<HTMLInputElement, EditStatusAutoCompleteProps>(
  ({defaultValue, onCharacterCountChange, organization, tone, setInputValue}, ref) => {
    const [suggesting, setSuggesting] = useState<string | undefined>(undefined)
    const emojiQuery = useEmojisQuery(suggesting === SUGGESTION_TYPES.EMOJI)
    const mentionSuggestions = useMentionsQuery(suggesting === SUGGESTION_TYPES.MENTIONS, organization)
    const {calculateSuggestions: calculateEmojiSuggestions, trigger: emojiTrigger} = useEmojiSuggestions(
      emojiQuery.isPending ? 'loading' : emojiQuery.data ?? emptyArray,
      {tone},
    )
    const {trigger: mentionsTrigger, calculateSuggestions: calculateMentionSuggestions} = useMentionSuggestions(
      mentionSuggestions.isPending ? 'loading' : mentionSuggestions.data ?? emptyArray,
    )

    const triggersAndSuggestions = useMemo(
      () => [
        {
          trigger: emojiTrigger,
          suggestionsCalculator: calculateEmojiSuggestions,
        },
        {trigger: mentionsTrigger, suggestionsCalculator: calculateMentionSuggestions},
      ],
      [emojiTrigger, calculateEmojiSuggestions, mentionsTrigger, calculateMentionSuggestions],
    )

    const {triggers, suggestions, setSuggestionEvent} = useAutocompleteTriggersAndSuggestions(triggersAndSuggestions)

    const onStatusChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const valueLength = getUtf8StringLength(e.target.value)
        onCharacterCountChange(valueLength)
        setInputValue?.(e.target.value)
      },
      [onCharacterCountChange, setInputValue],
    )

    return (
      <InlineAutocomplete
        inputRef={ref}
        fullWidth
        triggers={triggers}
        suggestions={suggestions}
        onShowSuggestions={event => {
          setSuggestionEvent(event)
          switch (event.trigger.triggerChar) {
            case emojiTrigger.triggerChar:
              setSuggesting(SUGGESTION_TYPES.EMOJI)
              break
            case mentionsTrigger.triggerChar:
              setSuggesting(SUGGESTION_TYPES.MENTIONS)
              break
            default:
              setSuggesting(undefined)
              break
          }
        }}
        onHideSuggestions={() => {
          setSuggestionEvent(null)
          setSuggesting(undefined)
        }}
      >
        <TextInput
          block
          name="message"
          defaultValue={defaultValue}
          maxLength={MESSAGE_MAX_LENGTH}
          onChange={onStatusChange}
          data-testid="user-status-message-input"
        />
      </InlineAutocomplete>
    )
  },
)

EditStatusAutoComplete.displayName = 'EditStatusAutoComplete'
